import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module, RecaptchaModule } from "ng-recaptcha";

// 3rd party controls
// https://www.npmjs.com/package/ngx-pagination
// import { NgxPaginationModule } from 'ngx-pagination';
import { ToastrModule } from 'ngx-toastr';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faLongArrowAltUp, faBoxOpen, faTimes, faList, faSlidersH, faGamepad, faStar, faPlusCircle,
  faMinusCircle, faTimesCircle, faShoppingCart, faAngleDown, faAngleUp, faChevronLeft, faChevronRight,
  faCheck, faCheckCircle, faSignOutAlt, faCreditCard, faArrowCircleDown, faUserTimes, faFileAlt, faCheckSquare, faSquare, faBan
} from '@fortawesome/free-solid-svg-icons';

import { faClock } from '@fortawesome/free-regular-svg-icons';

library.add(faBan, faLongArrowAltUp, faBoxOpen, faTimes, faList, faSlidersH, faGamepad, faStar, faPlusCircle,
  faMinusCircle, faTimesCircle, faShoppingCart, faAngleDown, faAngleUp, faChevronLeft, faChevronRight, faCheck, faCheckCircle, faSignOutAlt, faClock, faCreditCard, faArrowCircleDown, faUserTimes, faFileAlt, faCheckSquare, faSquare);

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';

import { CountdownModule } from 'ngx-countdown';

import { AppRoutingModule } from '../app-routing/app-routing.module';
import { AppComponent } from './app.component';

// custom controls
import { AlvBtnComponent } from '../../controls/alv-btn/alv-btn.component';
import { WeekCalendarPickerComponent } from '../../controls/week-calendar-picker/week-calendar-picker.component';
import { Angular2SignaturepadModule } from 'angular2-signaturepad';

// pipes
import { FilterPipe } from '../../pipes/filter.pipe';
import { SafePipe } from '../../pipes/safe.pipe';

// pages
import { DashboardComponent } from '../dashboard/dashboard.component';
import { RegisterComponent } from '../register/register.component';
import { GatewayComponent } from '../gateway/gateway.component';
import { ScanBarCodeComponent } from '../scan-bar-code/scan-bar-code.component';
import { OrderComponent } from '../order/order.component';
import { CustomerInfoComponent } from '../customer-info/customer-info.component';
import { PasswordInfoComponent } from '../password-info/password-info.component';
import { WaiverComponent } from '../waiver/waiver.component'
import { EmailInfoComponent } from '../email-info/email-info.component';
import { CheckoutComponent } from '../checkout/checkout.component';
import { CheckoutHeaderComponent } from '../checkout-header/checkout-header.component';
import { PaymentComponent } from '../payment/payment.component';
import { ReceiptComponent } from '../receipt/receipt.component';
import { ThankYouComponent } from '../thank-you/thank-you.component';
import { StripePaymentComponent } from 'src/app/common/stripe-payment/stripe-payment.component';

//common
import { OrderProductNameComponent } from '../common/order-product-name/order-product-name.component'

// section
import { CartComponent } from '../cart/cart.component';

// footer
import { FooterComponent } from '../footer/footer.component';
import { APP_BASE_HREF } from '@angular/common';
import { OnscreenKeyboardDirective } from 'src/app/directives/onscreen-keyboard.directive';
import { GuestSelectionComponent } from '../guest-selection/guest-selection.component';
import { GuestBasicinfoEditComponent } from '../guest-basicinfo-edit/guest-basicinfo-edit.component';
import { AddGuestsComponent } from '../add-guests/add-guests.component';
import { DatePickerComponent } from '../common/date-picker/date-picker.component';
import { GuestContactinfoEditComponent } from '../guest-contactinfo-edit/guest-contactinfo-edit.component';
import { SignWaiverComponent } from '../sign-waiver/sign-waiver.component';
import { NumberToWordsPipe } from 'src/app/pipes/number-to-words.pipe';
import { SignaturePadComponent } from '../signature-pad/signature-pad.component';
import { CustomFieldComponent } from '../custom-field/custom-field.component';
import { TenantConfigService } from 'src/app/services/tenant-config.service';
import { StripePaymentTerminalComponent } from '../stripe-payment-terminal/stripe-payment-terminal.component';

function appLoadFactory(config: TenantConfigService) {
  return () => config.loadConfig().then(() => console.log(`config resolved`, config));
}

@NgModule({
  declarations: [
    AppComponent

    // custom controls
    , AlvBtnComponent
    , WeekCalendarPickerComponent

    // pipes
    , FilterPipe
    , SafePipe
    , NumberToWordsPipe

    //directives
    , OnscreenKeyboardDirective

    // pages
    , DashboardComponent
    , RegisterComponent
    , GatewayComponent
    , ScanBarCodeComponent
    , OrderComponent
    , CustomerInfoComponent
    , CheckoutComponent
    , CheckoutHeaderComponent
    , PaymentComponent
    , ReceiptComponent
    , ThankYouComponent
    , StripePaymentComponent
    , StripePaymentTerminalComponent

    //waiver
    , WaiverComponent
    , EmailInfoComponent
    , PasswordInfoComponent
    , GuestSelectionComponent
    , GuestBasicinfoEditComponent
    , AddGuestsComponent
    , GuestContactinfoEditComponent
    , SignWaiverComponent

    //common
    , DatePickerComponent
    , SignaturePadComponent
    , CustomFieldComponent
    , OrderProductNameComponent

    // section
    , CartComponent

    // footer
    , FooterComponent
  ],
  imports: [
    BrowserModule
    , HttpClientModule
    , BrowserAnimationsModule
    , FormsModule
    , RecaptchaV3Module
    , RecaptchaV3Module
    , RecaptchaModule
    , ReactiveFormsModule
    // , NgxPaginationModule
    , FontAwesomeModule
    , Angular2SignaturepadModule
    , ToastrModule.forRoot({
      timeOut: 2000,
      positionClass: 'toast-bottom-center',
      toastClass: 'ngx-toastr',
      // preventDuplicates: true,
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info',
        success: 'toast-success',
        warning: 'toast-warning'
      }
    })
    , AppRoutingModule
    , CountdownModule
  ],
  bootstrap: [AppComponent]
  , providers: [
    {
      provide: APP_BASE_HREF,
      useValue: '/kiosk'
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (config: TenantConfigService) => {
        return config.recaptchaSiteKeyV3;
      },
      deps: [TenantConfigService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appLoadFactory,
      deps: [TenantConfigService],
      multi: true,
    }]
})
export class AppModule { }
